/**
 * Sorting the follow-up queue into the four answers an operator acts on.
 *
 * The API already returns the rows soonest-first, so a flat list would work and
 * would be wrong. A queue of forty dates reads as forty equal obligations, and
 * the two that are late look exactly like the thirty-eight that are not. What
 * an operator needs before opening anything is "how much of this is my morning"
 * - which is a count per bucket, not a sort order.
 *
 * REACT-FREE ON PURPOSE (§4). The bucket rule is the one thing here that can be
 * wrong in a way nobody sees: an off-by-one at the "today" boundary shows a
 * company as due tomorrow on the day it was promised, and that is a bug you
 * find by getting a complaint, not by looking at a screen. A pure function with
 * a test is the whole reason this file exists apart from the component.
 */
import type { BrainDueRow } from "../../../lib/siteBrain";

/**
 * The four groups, in the order they are worked.
 *
 * "Later" is deliberately one bucket rather than a month/quarter split: past
 * the current week the exact distance stops changing what anybody does today,
 * and a fifth heading would only make the page longer.
 */
export const DUE_BUCKETS = ["overdue", "today", "week", "later"] as const;

export type DueBucket = (typeof DUE_BUCKETS)[number];

/**
 * Which bucket one row belongs in.
 *
 * `due` decides "overdue" and `inDays` decides nothing about it. The server
 * computes `due` from the actual timestamp and `inDays` from the same
 * timestamp ROUNDED to whole days, so a follow-up eight hours past its time has
 * `due: true` and `inDays: 0`. Reading the number instead of the flag would
 * file that row under "today" and it would never be chased.
 */
export function bucketOf(row: Pick<BrainDueRow, "due" | "inDays">): DueBucket {
  if (row.due) return "overdue";
  if (row.inDays <= 0) return "today";
  if (row.inDays <= 7) return "week";
  return "later";
}

/** One heading and the rows under it. */
export interface DueGroup {
  bucket: DueBucket;
  rows: BrainDueRow[];
}

/**
 * Group the rows, keeping the order the server sent them in.
 *
 * Empty buckets are dropped rather than rendered as headings with nothing
 * under them: a page whose first three sections are empty reads as broken even
 * when it is simply a quiet week.
 */
export function groupDue(rows: BrainDueRow[]): DueGroup[] {
  const byBucket = new Map<DueBucket, BrainDueRow[]>();
  for (const row of rows) {
    const bucket = bucketOf(row);
    const list = byBucket.get(bucket);
    if (list) list.push(row);
    else byBucket.set(bucket, [row]);
  }
  return DUE_BUCKETS.filter((b) => byBucket.has(b)).map((bucket) => ({ bucket, rows: byBucket.get(bucket)! }));
}

/**
 * A date `days` out at nine in the morning, as an ISO string.
 *
 * Same shape the card's follow-up box uses, and duplicated here rather than
 * imported from it for one reason: that box is a component, and pulling a
 * React module into this file would take the test with it. Nine o'clock rather
 * than the current time so "in a week" is a working day, not a moment.
 */
export function dayFromNow(days: number, now: Date = new Date()): string {
  const at = new Date(now);
  at.setHours(9, 0, 0, 0);
  at.setDate(at.getDate() + days);
  return at.toISOString();
}
